/**
 * bulkWhatsapp — helpers for the semi-manual bulk WhatsApp flow.
 *
 * V1 is intentionally frontend-only: builds wa.me deep-links, renders a
 * template with placeholders, and validates phone numbers well enough to
 * skip the leads that would fail on WhatsApp's side. Anything more (opt-out,
 * Business API, scheduled campaigns) is out of scope.
 */

// Placeholder configurable en un solo sitio para no dispersarlo por el UI.
// Se sustituye por el link real cuando marketing lo defina.
export const DOSSIER_LINK = "";

/**
 * Prefer lead.whatsapp when present (may not exist in V1 model), fall back
 * to lead.phone. Never invents digits.
 */
export function getLeadPhone(lead) {
  if (!lead) return "";
  return String(lead.whatsapp || lead.phone || "").trim();
}

/**
 * Return digits-only phone suitable for the wa.me path segment.
 * - strips spaces, dashes, parentheses, dots, the leading `+`
 * - converts a leading `00` international prefix to bare form
 * - returns "" if nothing usable is left
 */
export function normalizeWhatsappPhone(phone) {
  if (!phone) return "";
  let s = String(phone).trim();
  // Strip leading + so wa.me path stays clean.
  if (s.startsWith("+")) s = s.slice(1);
  // Digits only.
  let digits = s.replace(/\D+/g, "");
  // 00-prefixed international → drop the two leading zeros.
  if (digits.startsWith("00")) digits = digits.slice(2);
  return digits;
}

/**
 * A valid wa.me phone is at least 8 digits after normalization. This is
 * intentionally lenient — WhatsApp itself rejects garbage on open, but we
 * want to exclude obvious junk (empty, "123") from the queue.
 */
export function isValidWhatsappPhone(phone) {
  const digits = normalizeWhatsappPhone(phone);
  return digits.length >= 8;
}

const TEMPLATE_VARIABLES = ["nombre", "telefono", "email", "canal", "pais", "interes", "villa", "precio", "link_dossier"];

/**
 * Render a template. Placeholders are `{name}` style. Unknown placeholders
 * are replaced by an empty string so the message never leaks raw braces.
 */
export function renderWhatsappTemplate(template, lead) {
  const phone = normalizeWhatsappPhone(getLeadPhone(lead));
  const canal = lead?.preferred_contact_channel || lead?.source || "";
  const villa = lead?.villa_interest || lead?.villa || lead?.interest || "";
  const precio = lead?.budget_eur ? String(lead.budget_eur) : (lead?.price_interest || "");
  const values = {
    nombre: lead?.name || "",
    telefono: phone,
    email: lead?.email || "",
    canal,
    pais: lead?.country || "",
    interes: lead?.interest || "",
    villa,
    precio,
    link_dossier: DOSSIER_LINK,
  };
  return String(template || "").replace(/\{([a-z_]+)\}/g, (_, key) => {
    if (Object.prototype.hasOwnProperty.call(values, key)) return values[key];
    return "";
  });
}

export function whatsappTemplateVariables() {
  return TEMPLATE_VARIABLES.slice();
}

/**
 * Build the wa.me URL. Returns null if the phone can't be normalized to a
 * usable number so callers can skip the lead without extra checks.
 */
export function buildWhatsappUrl(phone, message) {
  const digits = normalizeWhatsappPhone(phone);
  if (!digits || digits.length < 8) return null;
  const body = encodeURIComponent(message || "");
  return `https://wa.me/${digits}?text=${body}`;
}

/**
 * Enlace wa.me para el atajo de un lead: sin mensaje previo, solo abrir la
 * conversación. Devuelve null cuando el lead no tiene un teléfono usable,
 * para que quien llama NO pinte un botón que parece funcionar y no funciona.
 *
 * Toma el teléfono canónico (`getLeadPhone`: whatsapp antes que phone) y lo
 * normaliza con las mismas reglas que el resto del producto — nadie debería
 * volver a escribir un `replace(/\D/g, "")` por su cuenta: ese se come el
 * prefijo internacional `00` y deja pasar un "123" que en WhatsApp falla.
 */
export function leadWhatsappHref(lead) {
  const digits = normalizeWhatsappPhone(getLeadPhone(lead));
  if (!isValidWhatsappPhone(digits)) return null;
  return `https://wa.me/${digits}`;
}

// Default template used when the modal opens with nothing set. Kept aligned
// with the "first_contact" preset in whatsappTemplates.js so both entry
// points render the same starting text.
export const DEFAULT_BULK_TEMPLATE =
  "Hola {nombre}, soy del equipo de Dolce Villas Lombok.\n\n" +
  "Te escribo porque dejaste tus datos interesado en nuestras villas en Kuta Lombok.\n\n" +
  "¿Te gustaría que te envíe el dossier con toda la información?";

// Fixed identifier for messages_log entries created by this flow so
// downstream analytics can distinguish them from the per-lead templates.
export const BULK_TEMPLATE_KEY = "bulk_whatsapp_v1";

// TODO(V2): honor a `do_not_contact` / `opt_out` flag on the lead. In V1
// the field does not exist; add it in a follow-up PR before rolling out
// wider outreach.

// ---------------------------------------------------------------------------
// Queue snapshot persistence (localStorage)
// ---------------------------------------------------------------------------
// Lets the modal survive a page reload / app-switch on mobile. The snapshot
// is scoped by the exact lead set — if the operator changes the selection
// while the modal is closed, the stale snapshot is silently ignored so we
// never restore into a mismatched context.

export const QUEUE_STORAGE_KEY = "dolce_bulk_whatsapp_queue_v1";
export const QUEUE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

// Canonical per-lead statuses in the queue. Kept in sync with the modal so
// callers can build/inspect snapshots without importing the component.
export const LEAD_QUEUE_STATUSES = ["pending", "opened", "sent_manual", "skipped", "invalid_phone"];

/**
 * Read the persisted queue snapshot. Returns null when nothing is stored,
 * the stored payload is malformed, or it has aged out past QUEUE_TTL_MS.
 * All access is wrapped in try/catch so a SecurityError (Safari private
 * browsing, sandboxed iframe) never crashes the modal.
 */
export function loadQueueSnapshot() {
  try {
    const raw = window.localStorage.getItem(QUEUE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const created = Date.parse(parsed.createdAt || "");
    if (!Number.isFinite(created)) return null;
    if (Date.now() - created > QUEUE_TTL_MS) return null;
    if (!Array.isArray(parsed.leadIds)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveQueueSnapshot(snapshot) {
  try {
    window.localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* quota / SecurityError — silently drop */
  }
}

export function clearQueueSnapshot() {
  try {
    window.localStorage.removeItem(QUEUE_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/**
 * True when two lead_id lists represent the same set, regardless of order.
 * Used to decide whether a stored snapshot still matches the current
 * selection before offering to resume.
 */
export function sameLeadSet(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const setA = new Set(a);
  for (const x of b) if (!setA.has(x)) return false;
  return true;
}
